/* eslint-disable react-refresh/only-export-components -- co-located options+helpers; HMR-only warning, no runtime impact */
// SAB-401: NFL mobile players position filter. Mirrors
// mlb-beta/players-mobile/PositionPriorityRow.tsx but:
//   - drops the Hitters/Pitchers "PlayerType" Seg (NFL doesn't have an
//     analogous orthogonal split; offense/defense IS the position filter)
//   - position list = NFLPlayersPage.tsx:42 exactly (no FLEX — that's NOVEL)
//   - min-w-[28px] on the trigger to keep 2-3 char NFL labels (WR, DST,
//     COACH) from cramping; matches the chip-width guardrail in the brief
import { Filter, ChevronDown } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

export const NFL_POSITION_FILTERS: { value: string; label: string }[] = [
  { value: 'All',   label: 'All Positions' },
  { value: 'QB',    label: 'QB' },
  { value: 'RB',    label: 'RB' },
  { value: 'WR',    label: 'WR' },
  { value: 'TE',    label: 'TE' },
  { value: 'K',     label: 'K' },
  { value: 'P',     label: 'P' },
  { value: 'DST',   label: 'DST' },
  { value: 'COACH', label: 'Coach' },
  { value: 'DB',    label: 'DB' },
  { value: 'LB',    label: 'LB' },
  { value: 'DL',    label: 'DL' },
];

const TRIGGER_LABEL_OVERRIDES: Record<string, string> = {
  All: 'All Pos',
};

export function getPositionTriggerLabel(value: string): string {
  if (TRIGGER_LABEL_OVERRIDES[value]) return TRIGGER_LABEL_OVERRIDES[value];
  // Falls through to the value itself, which is what makes the SAB-998 FLEX chip render
  // as "FLEX" without needing an entry here. Kept as a fallback rather than a lookup that
  // can miss, so a chip added to the scoped list can never display blank.
  return NFL_POSITION_FILTERS.find((g) => g.value === value)?.label ?? value;
}

interface PositionPriorityRowProps {
  posFilter: string;
  onPosFilterChange: (value: string) => void;
  /**
   * SAB-995 / SAB-998: the chips THIS league should offer, already scoped by
   * `useLeagueRosterability`. Optional so the component keeps working for any caller that
   * has no league in hand, defaulting to the full list -- the same "null means do not
   * filter" stance the rest of the scoping takes, since a short filter row is a worse
   * failure than a permissive one.
   */
  options?: { value: string; label: string }[];
}

export function PositionPriorityRow({
  posFilter,
  onPosFilterChange,
  options,
}: PositionPriorityRowProps) {
  const positionOptions = options ?? NFL_POSITION_FILTERS;
  const posActive = posFilter !== 'All';

  return (
    <div className="flex gap-2 items-stretch w-full min-w-0">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Position filter"
            className={cn(
              'flex items-center justify-center gap-1.5 px-3 h-[34px] rounded-full min-w-[88px]',
              'bg-transparent border text-[12px] font-semibold whitespace-nowrap shrink-0',
              'transition-[box-shadow,color] duration-150',
              'focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[hsl(var(--ring))]',
              posActive
                ? 'text-primary border-primary'
                : 'text-muted-foreground border-border',
            )}
          >
            <Filter className="w-3.5 h-3.5" />
            <span className="min-w-[28px] inline-block text-center">{getPositionTriggerLabel(posFilter)}</span>
            <ChevronDown className="w-3.5 h-3.5 opacity-70" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="max-h-[60vh] overflow-y-auto">
          {positionOptions.map((g) => (
            <DropdownMenuItem
              key={g.value}
              onClick={() => onPosFilterChange(g.value)}
              className={cn(g.value === posFilter && 'text-primary font-semibold')}
            >
              {g.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export default PositionPriorityRow;
